import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '@features/PageHeader';
import IntegrationRequired from '@features/IntegrationRequired';
import ReplyTemplateManageModal, { FORM_TYPE } from '@features/ReplyTemplateManageModal';
import { ModalConfirm } from '@components/Modal';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import { replyTemplatesActions } from '@actions';
import { useTranslation } from '@hooks';
import { replyTemplatesService } from '@services';
import { AccIntegrationName } from '@constants/integrations';
import { isIntegrationConnected } from '@helpers/integrations';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import usePermissions from '@hooks/usePermissions';
import { alertActions } from '@actions';
import { ReplyTemplatesIcon } from 'assets/images/icons';
import ReplyTemplateTable from './ReplyTemplateTable';

const ReplyTemplates = () => {
    const { t } = useTranslation();

    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [modal, setModal] = useState({ open: false, type: FORM_TYPE.NEW });
    const [pageTemplates, setPageTemplates] = useState(0);
    const [rowsPerPageTemplates, setRowsPerPageTemplates] = useState(DEFAULT_PAGE_SIZE);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

    const account = useSelector(state => state.account?.account);
    const { totalCount } = useSelector(state => state.replyTemplates);
    const dispatch = useDispatch();

    const { hasFullAccess } = usePermissions();
    const isUser = !hasFullAccess('reviews');
    const GOOGLE_CONNECTED = isIntegrationConnected(account, AccIntegrationName.Google);

    useEffect(() => {
        dispatch(replyTemplatesActions.get(pageTemplates, rowsPerPageTemplates));
    }, [pageTemplates, rowsPerPageTemplates, dispatch]);

    const onDelete = async () => {
        try {
            await replyTemplatesService.deleteReplyTemplate(selectedTemplate.id);
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            if (pageTemplates === 0) {
                await dispatch(replyTemplatesActions.get(pageTemplates, rowsPerPageTemplates));
            } else {
                setPageTemplates(0); // INFO: Triggers refetch of templates
            }
            setDeleteModalOpen(false);
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.deleteFail')));
        }
    };

    const onAddTemplateClick = () => setModal({ open: true, type: FORM_TYPE.NEW });

    const refetchFirstPage = async () => {
        if (pageTemplates === 0) {
            await dispatch(replyTemplatesActions.get(pageTemplates, rowsPerPageTemplates));
        } else {
            setPageTemplates(0); // INFO: Triggers refetch of templates
        }
    };

    const REFETCH_MAP = {
        [FORM_TYPE.EDIT]: async () => await dispatch(replyTemplatesActions.get(pageTemplates, rowsPerPageTemplates)),
        [FORM_TYPE.NEW]: () => refetchFirstPage(),
        [FORM_TYPE.EDIT_COPY]: () => refetchFirstPage(),
    };

    const onCloseModal = () => {
        setModal({ open: false, type: FORM_TYPE.NEW });
        setSelectedTemplate(null);
    };

    return (
        <>
            {!GOOGLE_CONNECTED ? (
                <IntegrationRequired googleOnly />
            ) : (
                <PageHeader
                    styles={{ marginBottom: '16px' }}
                    icon={<ReplyTemplatesIcon />}
                    title={t('ReviewsReplyTemplates.tenfoldProductivity')}
                    subtitle={t('ReviewsReplyTemplates.googleConnected')}
                    metrics={
                        <Typography variant="body1">
                            {t('ReviewsReplyTemplates.created')} {totalCount}
                        </Typography>
                    }
                >
                    {!isUser && (
                        <Button variant="contained" onClick={onAddTemplateClick}>
                            {t('buttons.addTemplate')}
                        </Button>
                    )}
                </PageHeader>
            )}
            {GOOGLE_CONNECTED && (
                <ReplyTemplateTable
                    setModal={setModal}
                    setSelectedTemplate={setSelectedTemplate}
                    setPage={setPageTemplates}
                    page={pageTemplates}
                    setRowsPerPage={setRowsPerPageTemplates}
                    rowsPerPage={rowsPerPageTemplates}
                    setDeleteModalOpen={setDeleteModalOpen}
                    isUser={isUser}
                />
            )}
            {modal.open ? (
                <ReplyTemplateManageModal
                    selectedTemplate={selectedTemplate}
                    modal={modal}
                    refetchCallback={REFETCH_MAP[modal.type]}
                    onCloseModal={onCloseModal}
                />
            ) : null}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => {
                        setDeleteModalOpen(false);
                        setSelectedTemplate(null);
                    }}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </>
    );
};

export default ReplyTemplates;
